/** Os campos, o aviso e a janela da Aplicação de Fontes Geradoras (#606). */
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Button, TextField } from '@mui/material';

import type { AvisoDosItensProps, JanelaDeDetalheProps } from '../../components/applications/tipos';
import { SourceApplicationModal } from '../../components/source-applications/SourceApplicationModal';
import { sourceApplicationService } from '../../services/sourceApplicationService';
import { SourceExposureLevel, type SourceApplicationData, type SourceWithApplication } from '../../types/sourceApplication';
import {
  NIVEIS_DE_EXPOSICAO,
  ROTULO_DA_EXPOSICAO,
  eInicioCompleto,
  enderecoDosRiscos,
  hoje,
  problemaDoInicio,
} from './textosDaFonte';

interface SeletorDeExposicaoProps {
  valor: SourceExposureLevel | '';
  rotulo: string;
  aoMudar: (novo: SourceExposureLevel) => void;
  /** Opção vazia de partida, para quando nada vem escolhido (ação em lote). */
  vazio?: string;
}

export function SeletorDeExposicao({ valor, rotulo, aoMudar, vazio }: SeletorDeExposicaoProps) {
  return (
    <TextField
      select
      size="small"
      value={valor}
      onChange={(evento) => aoMudar(evento.target.value as SourceExposureLevel)}
      SelectProps={{ native: true }}
      inputProps={{ 'aria-label': rotulo }}
      sx={{ minWidth: 140 }}
    >
      {vazio !== undefined && (
        <option value="" disabled>
          {vazio}
        </option>
      )}
      {NIVEIS_DE_EXPOSICAO.map((nivel) => (
        <option key={nivel} value={nivel}>
          {ROTULO_DA_EXPOSICAO[nivel]}
        </option>
      ))}
    </TextField>
  );
}

interface CampoDeInicioProps {
  valor: string;
  rotulo: string;
  aoMudar: (novo: string) => void;
}

export function CampoDeInicio({ valor, rotulo, aoMudar }: CampoDeInicioProps) {
  return (
    <TextField
      type="date"
      size="small"
      value={valor}
      onChange={(evento) => aoMudar(evento.target.value)}
      inputProps={{ 'aria-label': rotulo, max: hoje() }}
      sx={{ minWidth: 160 }}
    />
  );
}

interface InicioNaLinhaProps {
  valor: string;
  /** O fim gravado (AAAA-MM-DD), vazio quando não tem: o início não passa dele. */
  fim: string;
  rotulo: string;
  aoMudar: (novo: string) => Promise<boolean>;
}

/**
 * O início na edição em linha: grava ao sair do campo. O campo de data avisa a cada tecla (o ano
 * passa por 0002, 0020…), então só a data completa vai ao servidor; só o campo inteiramente vazio
 * grava sem início — com um pedaço apagado o navegador também entrega vazio, mas marca a entrada
 * como incompleta. O que não pode ser gravado — a meio da digitação, no futuro, depois do fim, ou
 * recusado pelo servidor — volta ao valor gravado, e o motivo fica no campo. A recusa do servidor
 * chega depois: se a pessoa já voltou a digitar, o que ela digitou fica.
 */
export function InicioNaLinha({ valor, fim, rotulo, aoMudar }: InicioNaLinhaProps) {
  const [digitado, setDigitado] = useState(valor);
  const [gravado, setGravado] = useState(valor);
  const [problema, setProblema] = useState<string | null>(null);
  const ultimoGravado = useRef(valor);
  useEffect(() => {
    ultimoGravado.current = valor;
  }, [valor]);
  if (gravado !== valor) {
    setGravado(valor);
    setDigitado(valor);
  }

  const voltar = (motivo: string | null) => {
    setDigitado(valor);
    setProblema(motivo);
  };

  const sair = async (incompleto: boolean) => {
    if (digitado === valor && !incompleto) return;
    if (incompleto || (digitado !== '' && !eInicioCompleto(digitado))) return voltar(null);
    const recusa = digitado === '' ? null : problemaDoInicio(digitado, fim);
    if (recusa) return voltar(recusa);
    const enviado = digitado;
    // A mensagem de quando o servidor recusa vem no aviso do painel.
    if (!(await aoMudar(enviado))) {
      setDigitado((atual) => (atual === enviado ? ultimoGravado.current : atual));
    }
  };

  return (
    <TextField
      type="date"
      size="small"
      value={digitado}
      onChange={(evento) => {
        setDigitado(evento.target.value);
        setProblema(null);
      }}
      onBlur={(evento) => void sair(evento.target.validity.badInput)}
      error={problema !== null}
      helperText={problema ?? undefined}
      inputProps={{ 'aria-label': rotulo, max: fim !== '' && fim < hoje() ? fim : hoje() }}
      sx={{ minWidth: 160 }}
    />
  );
}

/** A fonte diz de onde o risco vem e não aplica o risco: quem aplica a fonte ainda aplica o risco. */
export function AvisoDaFonte(props: AvisoDosItensProps) {
  return (
    <Alert
      severity="info"
      sx={{ mb: 2, alignItems: 'center' }}
      action={
        <Button component={RouterLink} to={enderecoDosRiscos(props)} variant="outlined" size="small">
          Ir para Aplicação de Riscos
        </Button>
      }
    >
      <strong>A fonte geradora diz de onde o risco vem</strong> — uma máquina, um processo —{' '}
      <strong>mas não aplica o risco.</strong> Depois de aplicar a fonte, aplique também os riscos dela no mesmo
      destino.
    </Alert>
  );
}

export function DetalheDaFonte({ aberta, aplicacao, destino, caminho, aoFechar, aoSalvar }: JanelaDeDetalheProps<SourceWithApplication>) {
  const salvar = async (dados: SourceApplicationData) => {
    await sourceApplicationService.updateForTarget(destino.nivel, destino.id, Number(aplicacao.sourceGeneratorId), dados);
    aoSalvar();
  };
  return (
    <SourceApplicationModal
      open={aberta}
      onClose={aoFechar}
      onConfirm={salvar}
      source={aplicacao}
      mode="edit"
      destino={destino}
      caminho={caminho}
    />
  );
}
